
import { defineComponent, onMounted, onUnmounted, reactive, toRefs } from "vue";
import { fetchAlertHistory, fetchThresholds } from "@/helpers/api";
import { formatTime, formatDuration } from "@/helpers/time";
import { diagnosisLink } from "@/helpers/helpers";
import { SeverityLevel, ThresholdSelector } from "@/interfaces/Threshold";
import { PermissionsError } from "@/errors/errors";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import VueFeather from "vue-feather";

interface AlarmInstance {
  thresholdName: string;
  severity: SeverityLevel | null;
  asset: string;
  blade: string;
  node: string;
  time: number;
  timeEnd: number;
  durationMs: number;
  dashboardUid: string | null;
  selector?: ThresholdSelector;
}

const HISTORY_DAYS = 7;
const MAX_ROWS = 15;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export default defineComponent({
  name: "HomePage",
  components: {
    LoadingSpinner,
    VueFeather,
  },
  setup() {
    const state = reactive({
      alarms: [] as AlarmInstance[],
      hiddenCount: 0,
      alarmsLoading: true,
      alarmsConfigured: false,
      alarmsAccessible: true,
      grafanaError: "",
      iframeLoaded: false,
      historyDays: HISTORY_DAYS,
    });

    const loadAlarms = async () => {
      try {
        const thresholds = await fetchThresholds();
        state.alarmsAccessible = true;
        state.alarmsConfigured = thresholds.length > 0;
        if (thresholds.length === 0) {
          state.alarms = [];
          state.hiddenCount = 0;
          return;
        }

        const now = Math.floor(Date.now() / 1000);
        const from = now - HISTORY_DAYS * 24 * 60 * 60;
        let grafanaError = "";
        const responses = await Promise.all(
          thresholds.map(async (threshold) => {
            try {
              const response = await fetchAlertHistory(threshold.ThresholdID as number, from, now);
              return { threshold, history: response.history, grafanaError: response.grafanaError };
            } catch (error) {
              const message = error instanceof Error ? error.message : "Failed to fetch alert history";
              return { threshold, history: [], grafanaError: message };
            }
          })
        );

        const instances: AlarmInstance[] = [];
        for (const { threshold, history, grafanaError: responseError } of responses) {
          if (responseError && !grafanaError) {
            grafanaError = responseError;
          }
          // Selector arrives as a JSON string; the diagnosis link reads the waterline percentile out of it
          let selector: ThresholdSelector | undefined;
          try {
            selector = JSON.parse(threshold.Selector) as ThresholdSelector;
          } catch {
            selector = undefined;
          }
          for (const entry of history) {
            instances.push({
              thresholdName: threshold.Name,
              severity: threshold.Severity,
              asset: entry.asset,
              blade: entry.blade,
              node: entry.node,
              time: entry.time,
              timeEnd: entry.timeEnd,
              durationMs: entry.durationMs,
              dashboardUid: threshold.DashboardUID,
              selector,
            });
          }
        }
        state.grafanaError = grafanaError;

        // Ongoing alarms always shown first; resolved ones fill the remaining rows up to the cap
        const ongoing = instances.filter((i) => i.timeEnd === 0).sort((a, b) => b.time - a.time);
        const resolved = instances.filter((i) => i.timeEnd !== 0).sort((a, b) => b.time - a.time);
        const shown = ongoing.concat(resolved.slice(0, Math.max(0, MAX_ROWS - ongoing.length)));
        state.alarms = shown;
        state.hiddenCount = instances.length - shown.length;
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.alarmsAccessible = false;
        }
      } finally {
        state.alarmsLoading = false;
      }
    };

    // Auto-size the iframe to the dashboard's content so it never scrolls internally (expanding a
    // collapsed row grows the iframe instead). Polls Grafana's react-grid-layout container, which
    // carries an explicit height; re-querying each tick survives Grafana replacing the element on
    // row collapse/expand. If a future Grafana renames it, the fixed CSS height stays in effect.
    const GRAFANA_CHROME_PX = 112; // kiosk submenu (variables/time picker) + page padding, with slack
    const NOSCROLL_STYLE_ID = "home-iframe-noscroll";
    let heightTimer = 0;

    const syncDashboardHeight = () => {
      const iframe = document.getElementById("farm-overview-iframe") as HTMLIFrameElement | null;
      const container = iframe?.closest(".grafanaContainer") as HTMLElement | null;
      const doc = iframe?.contentDocument;
      const grid = doc?.querySelector(".react-grid-layout");
      if (!container || !doc || !grid) {
        return;
      }
      // While we control the height, the iframe body must never be a scroll target. Injected only
      // when the grid is found, so the CSS-fallback path keeps the body scrollbar and nothing gets
      // clipped.
      if (!doc.getElementById(NOSCROLL_STYLE_ID)) {
        const style = doc.createElement("style");
        style.id = NOSCROLL_STYLE_ID;
        style.textContent = "body { overflow-y: hidden !important; }";
        doc.head?.appendChild(style);
      }
      // Measure content as the lowest panel's bottom edge, not the grid box: Grafana stretches the
      // grid to fill the iframe viewport, so the grid's own height can grow but never shrink.
      const gridTop = grid.getBoundingClientRect().top;
      let contentBottom = gridTop;
      grid.querySelectorAll(".react-grid-item").forEach((item) => {
        contentBottom = Math.max(contentBottom, item.getBoundingClientRect().bottom);
      });
      const height = Math.ceil(contentBottom - gridTop) + GRAFANA_CHROME_PX;
      if (height > 300 && Math.abs(height - container.getBoundingClientRect().height) > 2) {
        container.style.height = `${height}px`;
      }
    };

    const onIframeLoad = () => {
      state.iframeLoaded = true;
      heightTimer = window.setInterval(syncDashboardHeight, 250);
    };

    let refreshTimer = 0;

    onMounted(() => {
      loadAlarms();
      refreshTimer = window.setInterval(loadAlarms, REFRESH_INTERVAL_MS);
    });

    onUnmounted(() => {
      window.clearInterval(refreshTimer);
      window.clearInterval(heightTimer);
    });

    return {
      ...toRefs(state),
      onIframeLoad,
      formatTime,
      formatDuration,
      diagnosisLink,
    };
  },
});
